import { format, isAfter, isBefore, startOfDay, subYears } from 'date-fns';
import { z } from 'zod';

/** tool_fund_transaction_type 2, "Deduction - deduction due to a purchase". */
export const PURCHASE_DEDUCTION_TYPE_ID = 2;

/** A recorded purchase is a tool fund request (type 1) created pending, then approved (2). */
export const TOOL_FUND_REQUISITION_TYPE_ID = 1;
export const PENDING_STATUS_ID = 1;
export const APPROVED_STATUS_ID = 2;

export const adminToolPurchaseSchema = z.object({
  lines: z
    .array(
      z.object({
        catalogItemId: z.number().min(1, 'Pick a tool'),
        itemName: z.string().trim().min(1, 'Tool name is required'),
        amount: z.number().min(0.01, 'Amount must be greater than 0'),
        received: z.boolean(),
        // As on approval: a purchase date within the last year, and a receipt.
        purchaseDate: z
          .date({ required_error: 'Purchase date is required' })
          .refine((date) => !isAfter(startOfDay(date), startOfDay(new Date())), {
            message: "Purchase date can't be in the future",
          })
          .refine((date) => !isBefore(startOfDay(date), startOfDay(subYears(new Date(), 1))), {
            message: "Purchase date can't be more than 1 year ago",
          }),
        receipt: z.instanceof(File, { message: 'Add the receipt' }).nullable().refine(Boolean, {
          message: 'Add the receipt',
        }),
        assetTag: z.string().trim().max(255, 'Too long: max length is 255 characters'),
        internalPurchase: z.boolean(),
        photo: z.instanceof(File).nullable(),
      }),
    )
    .min(1, 'Add at least one tool'),
  notes: z.string(),
});

export type AdminToolPurchaseFormData = z.infer<typeof adminToolPurchaseSchema>;
export type AdminToolPurchaseLineData = AdminToolPurchaseFormData['lines'][number];

/** A new tool row. The purchase date starts as today. */
export const makeEmptyPurchaseLine = (): AdminToolPurchaseLineData => ({
  catalogItemId: 0,
  itemName: '',
  amount: 0,
  received: false,
  purchaseDate: startOfDay(new Date()),
  receipt: null,
  assetTag: '',
  internalPurchase: false,
  photo: null,
});

export const purchaseTotal = (lines: Pick<AdminToolPurchaseLineData, 'amount'>[]) =>
  Math.round(lines.reduce((total, line) => total + (line.amount || 0), 0) * 100) / 100;

/** Same rule as the server: the total can't be more than the available balance. */
export const exceedsBalance = (total: number, availableBalance: number) =>
  Math.round(total * 100) > Math.round(availableBalance * 100);

/** The requisition items CreateRequisition takes, built as the request form builds them. */
export const toPurchaseItems = (data: AdminToolPurchaseFormData) =>
  data.lines.map((line) => ({
    catalogItemId: line.catalogItemId,
    customItemName: line.itemName.trim(),
    customItemDescription: data.notes.trim(),
    estimatedCost: line.amount,
    quantityRequested: 1,
    status: PENDING_STATUS_ID,
    internalPurchase: line.internalPurchase,
  }));

/** Each line's uploaded files: its receipt, and its photo when it has one. */
export type PurchaseLineFiles = { receiptId: number; photoId?: number };

/**
 * What approving one line sends, as the approval screen sends it (useRequisitionStatusUpdate):
 * status, purchase date, files (receipt then photo), who approved it, plus the cost, the purchased
 * name and the asset tag. The server creates the asset and the deduction from these.
 */
export const toLineApproval = (
  line: Pick<AdminToolPurchaseLineData, 'purchaseDate' | 'assetTag' | 'amount' | 'itemName'>,
  files: PurchaseLineFiles,
  approvedBy: number,
  now: Date,
) => ({
  statusId: APPROVED_STATUS_ID,
  fulfillmentDate: format(line.purchaseDate, 'yyyy-MM-dd'),
  updatedAt: format(now, 'yyyy-MM-dd HH:mm:ss'),
  fulfilledByUserId: approvedBy,
  fileIds: [files.receiptId, files.photoId].filter(Boolean).join(','),
  actualCost: line.amount,
  actualItemName: line.itemName.trim(),
  assetTag: line.assetTag.trim(),
  fieldMask: [
    'FileIds',
    'status_id',
    'UpdatedAt',
    'FulfilledByUserId',
    'FulfillmentDate',
    'ActualCost',
    'ActualItemName',
    'AssetTag',
  ],
});

/**
 * The fulfillments in line order. The server writes one fulfillment per line, in order, so the
 * lowest fulfillment id is the first line.
 */
export const fulfillmentsInLineOrder = <T extends { id: number }>(fulfillments: T[]) =>
  [...fulfillments].sort((a, b) => a.id - b.id);

/** The fulfillments of the lines marked received. */
export const receivedFulfillmentIds = (
  lines: Pick<AdminToolPurchaseLineData, 'received'>[],
  fulfillments: { id: number }[],
) => {
  const ordered = fulfillmentsInLineOrder(fulfillments);
  return lines.flatMap((line, i) => (line.received && ordered[i] ? [ordered[i].id] : []));
};
